// ============================================
// moldClaw Pricing Page
// ============================================

/**
 * 🧑‍💻 SWE-1: 가격 페이지 UI
 * 🧑‍💻 SWE-4: Polar Checkout 연동
 * 🎨 CX-1: UX 리뷰 적용
 * ✍️ CX-2: 카피라이팅
 * 🔧 UX-PRICING: 요금제 검수 및 개선 (2026-03-16)
 *   - 가격 원화 통일
 *   - 부가세 정보 추가
 *   - 결제 보안 뱃지 추가
 *   - 환불 정책 링크 추가
 *   - 연간/월간 토글 (준비 중)
 *   - 기능 비교표 추가
 * 🔧 POLAR: Polar Checkout 연동 (2026-03-16)
 */

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLicense, TIER_INFO } from '../hooks/useLicense';
import { usePayment } from '../hooks/usePayment';

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentLicense } = useLicense();
  const { 
    checkout, 
    isLoading: paymentLoading, 
    error: paymentError, 
    clearError,
    hasActiveSubscription,
    subscription,
  } = usePayment();
  const [loadingTier, setLoadingTier] = useState<'starter' | 'pro' | 'team' | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  
  // 에러 발생 시 5초 후 자동 클리어
  useEffect(() => {
    if (paymentError) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [paymentError, clearError]);
  
  const handleCheckout = async (tier: 'starter' | 'pro' | 'team') => {
    // 로그인 체크
    if (!user) {
      navigate('/login', { state: { from: '/pricing', tier } });
      return;
    }
    
    setLoadingTier(tier);
    
    try {
      // Polar Checkout으로 redirect
      await checkout(tier);
    } catch {
      // 에러는 usePayment에서 처리됨
      setLoadingTier(null);
    }
  };
  
  // 현재 티어 판별 (구독 우선, 라이선스 fallback)
  const currentTier: string = subscription?.tier || currentLicense?.tier || 'free';
  const isCurrentTier = (tier: string): boolean => currentTier === tier;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            심플하고 투명한 가격
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            필요에 맞는 플랜을 선택하세요. 언제든 업그레이드하거나 취소할 수 있습니다.
          </p>
        </div>
        
        {/* 💰 Billing Cycle Toggle */}
        <div className="flex justify-center mb-10">
          <div className="bg-gray-800 rounded-xl p-1 inline-flex items-center">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-space-blue text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              월간 결제
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-6 py-2 rounded-lg text-sm font-medium transition-all relative ${
                billingCycle === 'yearly'
                  ? 'bg-space-blue text-white'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              연간 결제
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                2개월 무료
              </span>
            </button>
          </div>
        </div>
        
        {/* ⚠️ 연간 결제 준비 중 안내 */}
        {billingCycle === 'yearly' && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg text-yellow-400 text-center">
            <p className="font-medium">🚧 연간 결제 준비 중</p>
            <p className="text-sm mt-1 text-yellow-400/80">
              연간 결제 옵션은 곧 출시됩니다. 현재는 월간 결제만 가능합니다.
            </p>
          </div>
        )}
        
        {/* Error Message */}
        {paymentError && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-center">
            <div className="flex items-center justify-center gap-2">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{paymentError}</span>
            </div>
            <button 
              onClick={clearError}
              className="mt-2 text-sm text-red-300 hover:text-red-200 underline"
            >
              닫기
            </button>
          </div>
        )}
        
        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {/* Free Tier */}
          <PricingCard
            tier={TIER_INFO.free}
            billingCycle={billingCycle}
            isCurrent={isCurrentTier('free')}
            onSelect={() => navigate('/register')}
            disabled={!!user}
            buttonText={user ? '현재 플랜' : TIER_INFO.free.cta}
          />
          
          {/* Starter Tier */}
          <PricingCard
            tier={TIER_INFO.starter}
            billingCycle={billingCycle}
            isCurrent={isCurrentTier('starter')}
            onSelect={() => handleCheckout('starter')}
            loading={loadingTier === 'starter' || paymentLoading}
            disabled={isCurrentTier('starter') || isCurrentTier('pro') || isCurrentTier('team') || billingCycle === 'yearly' || hasActiveSubscription}
            buttonText={
              currentTier === 'starter' ? '현재 플랜' :
              currentTier === 'pro' || currentTier === 'team' ? '낮은 플랜' :
              hasActiveSubscription ? '이미 구독 중' :
              billingCycle === 'yearly' ? '준비 중' :
              TIER_INFO.starter.cta
            }
          />
          
          {/* Pro Tier */}
          <PricingCard
            tier={TIER_INFO.pro}
            billingCycle={billingCycle}
            isCurrent={isCurrentTier('pro')}
            onSelect={() => handleCheckout('pro')}
            loading={loadingTier === 'pro' || paymentLoading}
            disabled={isCurrentTier('pro') || isCurrentTier('team') || billingCycle === 'yearly' || hasActiveSubscription}
            buttonText={
              currentTier === 'pro' ? '현재 플랜' :
              currentTier === 'team' ? '낮은 플랜' :
              hasActiveSubscription ? '이미 구독 중' :
              billingCycle === 'yearly' ? '준비 중' :
              TIER_INFO.pro.cta
            }
          />
          
          {/* Team Tier */}
          <PricingCard
            tier={TIER_INFO.team}
            billingCycle={billingCycle}
            isCurrent={isCurrentTier('team')}
            onSelect={() => handleCheckout('team')}
            loading={loadingTier === 'team' || paymentLoading}
            disabled={isCurrentTier('team') || billingCycle === 'yearly'}
            buttonText={
              currentTier === 'team' ? '현재 플랜' :
              billingCycle === 'yearly' ? '준비 중' :
              TIER_INFO.team.cta
            }
          />
        </div>
        
        {/* 💰 가격 안내 */}
        <div className="text-center mt-6 text-gray-500 text-sm">
          <p>표시된 가격은 USD 기준입니다. 결제 시 해당 국가의 세금이 추가될 수 있습니다.</p>
        </div>
        
        {/* 🔒 보안 뱃지 */}
        <SecurityBadges />
        
        {/* 📊 기능 비교표 */}
        <FeatureComparisonTable />
        
        {/* FAQ / Additional Info */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-white text-center mb-8">
            자주 묻는 질문
          </h2>
          
          <div className="space-y-6">
            <FaqItem
              question="언제든 플랜을 변경할 수 있나요?"
              answer="네, 언제든지 업그레이드하거나 다운그레이드할 수 있습니다. 업그레이드 시 즉시 적용되며, 다운그레이드는 다음 결제 주기부터 적용됩니다."
            />
            <FaqItem
              question="환불 정책은 어떻게 되나요?"
              answer="구독 후 7일 이내 사용량이 적은 경우 전액 환불해 드립니다. 고객 지원팀에 문의해 주세요."
              link={{ text: '환불 정책 전문 보기 →', href: '/terms#refund' }}
            />
            <FaqItem
              question="기기 수 제한은 어떻게 작동하나요?"
              answer="플랜에 따라 동시에 등록할 수 있는 기기 수가 다릅니다. 기기를 교체하고 싶으면 대시보드에서 기존 기기를 삭제한 후 새 기기를 등록하면 됩니다."
            />
            <FaqItem
              question="결제 수단은 어떤 것을 사용할 수 있나요?"
              answer="Visa, Mastercard, 국내 신용카드/체크카드를 사용할 수 있습니다. 결제는 Polar를 통해 안전하게 처리됩니다."
            />
          </div>
        </div>
        
        {/* 📞 고객 지원 섹션 */}
        <CustomerSupportSection />
        
        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-gray-400 mb-4">
            아직 결정하기 어려우신가요?
          </p>
          <button
            onClick={() => navigate('/register')}
            className="text-space-blue-light hover:text-space-blue-light font-medium transition-colors"
          >
            무료로 시작하기 →
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Pricing Card Component
// ============================================

interface PricingCardProps {
  tier: typeof TIER_INFO['free'] | typeof TIER_INFO['starter'] | typeof TIER_INFO['pro'] | typeof TIER_INFO['team'];
  billingCycle: 'monthly' | 'yearly';
  isCurrent: boolean;
  onSelect: () => void;
  loading?: boolean;
  disabled?: boolean;
  buttonText: string;
}

function PricingCard({ tier, billingCycle, isCurrent, onSelect, loading, disabled, buttonText }: PricingCardProps) {
  const isYearly = billingCycle === 'yearly';
  const displayPrice = isYearly && 'priceYearlyLabel' in tier 
    ? tier.priceYearlyLabel 
    : tier.priceLabel;
  const period = isYearly ? '/년' : tier.period;
  
  return (
    <div
      className={`relative rounded-2xl p-8 ${
        tier.popular
          ? 'bg-space-blue ring-4 ring-space-blue ring-opacity-50'
          : 'bg-gray-800'
      } ${isCurrent ? 'ring-2 ring-green-500' : ''}`}
    >
      {/* Popular Badge */}
      {tier.popular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-sm font-semibold px-4 py-1 rounded-full shadow-lg">
            🔥 인기
          </span>
        </div>
      )}
      
      {/* Current Badge */}
      {isCurrent && (
        <div className="absolute -top-4 right-4">
          <span className="bg-green-500 text-white text-sm font-semibold px-3 py-1 rounded-full">
            현재
          </span>
        </div>
      )}
      
      {/* Tier Name */}
      <h3 className={`text-xl font-bold ${tier.popular ? 'text-white' : 'text-gray-200'}`}>
        {tier.name}
      </h3>
      
      {/* Description */}
      <p className={`mt-2 text-sm ${tier.popular ? 'text-space-blue-light' : 'text-gray-400'}`}>
        {tier.description}
      </p>
      
      {/* Price */}
      <div className="mt-6 flex items-baseline">
        <span className={`text-4xl font-bold ${tier.popular ? 'text-white' : 'text-gray-100'}`}>
          {displayPrice}
        </span>
        {period && (
          <span className={`ml-1 text-lg ${tier.popular ? 'text-space-blue-light' : 'text-gray-500'}`}>
            {period}
          </span>
        )}
      </div>
      
      {/* Yearly Savings Badge */}
      {isYearly && 'yearlySavings' in tier && tier.yearlySavings && (
        <div className="mt-2">
          <span className="inline-block bg-green-500/20 text-green-400 text-xs px-2 py-1 rounded-full">
            {tier.yearlySavings}
          </span>
        </div>
      )}
      
      {/* Features */}
      <ul className="mt-8 space-y-3">
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <CheckIcon className={`h-5 w-5 flex-shrink-0 ${tier.popular ? 'text-space-blue-light' : 'text-green-500'}`} />
            <span className={`ml-3 text-sm ${tier.popular ? 'text-white' : 'text-gray-300'}`}>
              {feature}
            </span>
          </li>
        ))}
        {tier.limitations.map((limitation, index) => (
          <li key={`limit-${index}`} className="flex items-start">
            <XIcon className="h-5 w-5 flex-shrink-0 text-gray-500" />
            <span className="ml-3 text-sm text-gray-500">
              {limitation}
            </span>
          </li>
        ))}
      </ul>
      
      {/* CTA Button */}
      <button
        onClick={onSelect}
        disabled={disabled || loading}
        className={`mt-8 w-full py-3 px-4 rounded-lg font-semibold transition-all ${
          tier.popular
            ? 'bg-white text-space-blue hover:bg-gray-100 disabled:bg-gray-300'
            : 'bg-space-blue text-white hover:bg-space-blue hover:brightness-110 disabled:bg-gray-600'
        } disabled:cursor-not-allowed`}
      >
        {loading ? (
          <span className="flex items-center justify-center">
            <LoadingSpinner />
            처리 중...
          </span>
        ) : (
          buttonText
        )}
      </button>
    </div>
  );
}

// ============================================
// 🔒 Security Badges Component
// ============================================

function SecurityBadges() {
  return (
    <div className="mt-12 max-w-3xl mx-auto">
      <div className="bg-gray-800/50 rounded-xl p-6">
        <div className="text-center mb-4">
          <p className="text-sm text-gray-400">안전한 결제를 위해</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-6">
          {/* Polar 뱃지 */}
          <div className="flex items-center gap-2 text-gray-300">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6v6l4 2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span className="text-sm font-medium">Polar 결제</span>
          </div>
          
          {/* SSL 뱃지 */}
          <div className="flex items-center gap-2 text-gray-300">
            <svg className="w-6 h-6 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span className="text-sm font-medium">SSL 암호화</span>
          </div>
          
          {/* PCI DSS 뱃지 */}
          <div className="flex items-center gap-2 text-gray-300">
            <svg className="w-6 h-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <span className="text-sm font-medium">PCI DSS 준수</span>
          </div>
          
          {/* 카드사 로고 */}
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">VISA</span>
            <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">Mastercard</span>
            <span className="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded">국내카드</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// 📊 Feature Comparison Table
// ============================================

function FeatureComparisonTable() {
  const features = [
    { name: '월 토큰', free: '5M', starter: '50M', pro: '100M', team: '300M' },
    { name: '이미지 생성', free: '10개', starter: '80개', pro: '200개', team: '500개' },
    { name: '등록 가능 기기', free: '1대', starter: '2대', pro: '3대', team: '5대' },
    { name: 'AI 모델', free: 'Flash-Lite', starter: 'Flash-Lite', pro: 'Flash-Lite + Flash', team: '모든 모델' },
    { name: '상업적 사용', free: false, starter: true, pro: true, team: true },
    { name: '이메일 지원', free: false, starter: true, pro: true, team: true },
    { name: '우선 지원', free: false, starter: false, pro: true, team: true },
    { name: '사용량 대시보드', free: false, starter: false, pro: true, team: true },
    { name: '팀 관리 기능', free: false, starter: false, pro: false, team: true },
    { name: 'API 접근', free: false, starter: false, pro: false, team: true },
    { name: '전용 지원', free: false, starter: false, pro: false, team: true },
  ];
  
  return (
    <div className="mt-20 max-w-5xl mx-auto">
      <h2 className="text-2xl font-bold text-white text-center mb-8">
        플랜 비교
      </h2>
      
      <div className="bg-gray-800 rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className="text-left text-gray-400 text-sm font-medium px-6 py-4">기능</th>
              <th className="text-center text-gray-400 text-sm font-medium px-4 py-4">
                <span>Free</span>
                <span className="block text-xs text-gray-500">$0</span>
              </th>
              <th className="text-center text-gray-400 text-sm font-medium px-4 py-4">
                <span>Starter</span>
                <span className="block text-xs text-gray-500">$14.99/월</span>
              </th>
              <th className="text-center text-sm font-medium px-4 py-4">
                <span className="text-space-blue-light">Pro</span>
                <span className="block text-xs text-gray-500">$29.99/월</span>
              </th>
              <th className="text-center text-gray-400 text-sm font-medium px-4 py-4">
                <span>Team</span>
                <span className="block text-xs text-gray-500">$69.99/월</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature, index) => (
              <tr key={index} className="border-b border-gray-700/50 last:border-0">
                <td className="px-6 py-4 text-gray-300 text-sm">{feature.name}</td>
                <td className="px-4 py-4 text-center">
                  <FeatureValue value={feature.free} />
                </td>
                <td className="px-4 py-4 text-center">
                  <FeatureValue value={feature.starter} />
                </td>
                <td className="px-4 py-4 text-center bg-space-blue/5">
                  <FeatureValue value={feature.pro} highlight />
                </td>
                <td className="px-4 py-4 text-center">
                  <FeatureValue value={feature.team} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FeatureValue({ value, highlight = false }: { value: boolean | string; highlight?: boolean }) {
  if (typeof value === 'string') {
    return <span className={`text-sm ${highlight ? 'text-white font-medium' : 'text-gray-300'}`}>{value}</span>;
  }
  
  if (value) {
    return (
      <svg className={`w-5 h-5 mx-auto ${highlight ? 'text-space-blue-light' : 'text-green-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    );
  }
  
  return (
    <svg className="w-5 h-5 mx-auto text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

// ============================================
// 📞 Customer Support Section
// ============================================

function CustomerSupportSection() {
  return (
    <div className="mt-16 max-w-3xl mx-auto">
      <div className="bg-gray-800/50 rounded-xl p-6 text-center">
        <h3 className="text-lg font-semibold text-white mb-4">도움이 필요하신가요?</h3>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
          <a
            href="mailto:support@moldclaw.com"
            className="flex items-center gap-2 text-space-blue-light hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            support@moldclaw.com
          </a>
          <span className="hidden sm:inline text-gray-600">|</span>
          <Link
            to="/terms#refund"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            환불 정책
          </Link>
          <span className="hidden sm:inline text-gray-600">|</span>
          <Link
            to="/terms"
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            이용약관
          </Link>
        </div>
      </div>
    </div>
  );
}

// ============================================
// FAQ Item Component
// ============================================

interface FaqItemProps {
  question: string;
  answer: string;
  link?: { text: string; href: string };
}

function FaqItem({ question, answer, link }: FaqItemProps) {
  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h3 className="text-lg font-semibold text-white mb-2">{question}</h3>
      <p className="text-gray-400">{answer}</p>
      {link && (
        <Link
          to={link.href}
          className="inline-block mt-3 text-sm text-space-blue-light hover:text-white transition-colors"
        >
          {link.text}
        </Link>
      )}
    </div>
  );
}

// ============================================
// Icons
// ============================================

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function LoadingSpinner() {
  return (
    <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}
